import { useEffect, useMemo, useState } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import type { PartialBlock } from '@blocknote/core';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/shadcn/style.css';

type EditorTheme = 'light' | 'dark';

function readDocumentTheme(): EditorTheme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.classList.contains('light-mode') ? 'light' : 'dark';
}

// The admin panel toggles `light-mode` / `dark-mode` classes on <html> rather than
// the shadcn `.dark` class, so we observe those and pass an explicit theme prop.
function useAdminEditorTheme(): EditorTheme {
  const [theme, setTheme] = useState<EditorTheme>(readDocumentTheme);

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => setTheme(readDocumentTheme()));
    observer.observe(target, { attributes: true, attributeFilter: ['class'] });
    setTheme(readDocumentTheme());
    return () => observer.disconnect();
  }, []);

  return theme;
}

function normalizeInitialContent(content: unknown): PartialBlock[] | undefined {
  if (Array.isArray(content) && content.length > 0) {
    return content as PartialBlock[];
  }
  return undefined;
}

interface NoteEditorProps {
  initialContent: unknown;
  editable?: boolean;
  onChange?: (content: PartialBlock[]) => void;
}

export function NoteEditor({ initialContent, editable = true, onChange }: NoteEditorProps) {
  const theme = useAdminEditorTheme();
  const normalized = useMemo(() => normalizeInitialContent(initialContent), [initialContent]);

  const editor = useCreateBlockNote({ initialContent: normalized });

  return (
    <BlockNoteView
      editor={editor}
      editable={editable}
      theme={theme}
      onChange={() => onChange?.(editor.document)}
    />
  );
}

export default NoteEditor;
